import { LogoBanner } from "@/components/LogoBanner";
import { PillButton } from "@/game/play/panelControls";
import {
  normalizeJoinSlug,
  useConvexJoin,
  type JoinStatus,
} from "@/hooks/useConvexJoin";
import { LoaderCircle } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

const messages: Record<Exclude<JoinStatus, "joined" | "loading">, string> = {
  "not-found": "This shared game was not found or is no longer available.",
  offline: "You’re offline. Connect to the internet to join this game.",
  error: "We couldn’t join this game. Check your connection and try again.",
};

/** Joins the shared game in a link someone opened, with a way to retry. */
const Join = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { status, retry } = useConvexJoin(slug);

  if (status === "joined") return <Navigate to="/play" replace />;

  return (
    <main className="bg-suit-black min-h-dvh">
      <LogoBanner className="pt-safe-min-4 fixed" />
      {/* Where the game card sits on the play screen, in the panels' dark
          glass. */}
      <div className="pb-safe-min-4 fixed inset-x-0 bottom-0 mx-auto max-w-md px-4">
        <div className="glass-panel animate-card-in flex origin-bottom flex-col items-center gap-5 rounded-[32px] p-6 text-center text-white">
          <h1 className="text-[22px] font-bold">Join CourtShuffle</h1>
          {status === "loading" ? (
            <p className="flex items-center gap-3 text-white/70">
              <LoaderCircle className="size-6 animate-spin" aria-hidden />
              Joining game {normalizeJoinSlug(slug)}…
            </p>
          ) : (
            <>
              <p className="text-white/70">{messages[status]}</p>
              <div className="grid w-full grid-cols-2 gap-3">
                <PillButton
                  tinted={false}
                  className="min-h-14 text-xl"
                  onClick={() => navigate("/")}
                >
                  Home
                </PillButton>
                <PillButton className="min-h-14 text-xl" onClick={retry}>
                  Try Again
                </PillButton>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export { Join };
